// Vault key material (docs/runbooks/lately-shared-auth.md, "The vault").
//
// Everything here is plain WebCrypto so the same code runs in the page, in
// the core worker and in the service worker, and under bun for the tests.
//
// Key hierarchy (all labels are frozen; changing one re-enrols every device):
//
//   prf.results.first  ──HKDF(salt "lately.org/vault", info "kek/v1")──▶ KEK
//   DEK  = 32 random bytes, generated once per vault
//   slot = AES-256-GCM(KEK, DEK)                  one per passkey credential
//   SK   = non-extractable AES-GCM CryptoKey kept in IndexedDB (silent unlock)
//   session = AES-256-GCM(SK, DEK)
//   store key    = HKDF(DEK, info "store/v1")     the SQLite codec key (core)
//   accounts key = HKDF(DEK, info "accounts/v1")  seals the account roster
//
// The DEK itself never rests anywhere unwrapped; the page holds it in memory
// for the session and hands the worker only the derived store key.

export const VAULT_HKDF_SALT = "lately.org/vault";
export const KEK_INFO = "kek/v1";
export const STORE_KEY_INFO = "store/v1";
export const ACCOUNTS_KEY_INFO = "accounts/v1";
// The PRF evaluation input (`prf.eval.first`). Authenticators hash it with the
// WebAuthn PRF prefix, so any stable byte string works; this one names its
// version so a v2 can coexist with v1 in a later slot.
export const PRF_SALT_V1 = utf8("lately.org/vault/prf/v1");

export const DEK_BYTES = 32;
const GCM_IV_BYTES = 12;

export interface SealedBox {
  // base64url
  iv: string;
  ct: string;
}

export function utf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

export function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64Url(text: string): Uint8Array {
  const padded = text
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(text.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function generateDek(): Uint8Array {
  return randomBytes(DEK_BYTES);
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

// Copies into a fresh ArrayBuffer-backed view; WebCrypto rejects views over
// SharedArrayBuffer and some runtimes reject views with an offset.
function plain(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

async function hkdfKey(ikm: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", plain(ikm), "HKDF", false, ["deriveKey", "deriveBits"]);
}

function hkdfParams(info: string): HkdfParams {
  return {
    name: "HKDF",
    hash: "SHA-256",
    salt: plain(utf8(VAULT_HKDF_SALT)),
    info: plain(utf8(info)),
  };
}

// KEK = HKDF-SHA256(prf.results.first, salt "lately.org/vault", info "kek/v1"),
// as a non-extractable AES-256-GCM key: it only ever wraps/unwraps the DEK.
export async function deriveKek(prfOutput: Uint8Array): Promise<CryptoKey> {
  const base = await hkdfKey(prfOutput);
  return crypto.subtle.deriveKey(
    hkdfParams(KEK_INFO),
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// Raw 32-byte subkey of the DEK (the SQLite codec key handed to the core).
export async function deriveStoreKey(dek: Uint8Array): Promise<Uint8Array> {
  const base = await hkdfKey(dek);
  const bits = await crypto.subtle.deriveBits(hkdfParams(STORE_KEY_INFO), base, 256);
  return new Uint8Array(bits);
}

// AES-GCM subkey of the DEK that seals the account roster (page side).
export async function deriveAccountsKey(dek: Uint8Array): Promise<CryptoKey> {
  const base = await hkdfKey(dek);
  return crypto.subtle.deriveKey(
    hkdfParams(ACCOUNTS_KEY_INFO),
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// The browser-guarded session key: non-extractable, so it can be stored in
// IndexedDB as a CryptoKey object and used, but never read out.
export async function generateSessionKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function seal(key: CryptoKey, plaintext: Uint8Array, aad: string): Promise<SealedBox> {
  const iv = randomBytes(GCM_IV_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: plain(iv), additionalData: plain(utf8(aad)) },
    key,
    plain(plaintext),
  );
  return { iv: toBase64Url(iv), ct: toBase64Url(new Uint8Array(ct)) };
}

export async function open(key: CryptoKey, box: SealedBox, aad: string): Promise<Uint8Array> {
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: plain(fromBase64Url(box.iv)), additionalData: plain(utf8(aad)) },
    key,
    plain(fromBase64Url(box.ct)),
  );
  return new Uint8Array(pt);
}
